import { Client, Protocol } from '@tencent/mega-ipc-renderer';
import {
    DocType,
    DocxServiceProtocol,
    DumpPerformancePoint,
    LoadPerformancePoint,
    MegaReportServiceProtocol,
    ReportLoadOptions,
    ReportOptions,
    SaveType,
} from '@tencent/mega-sdk-protocols';
import { ILocalEditEvents, LocalEditEvents } from '../core/interfaces/local-edit-events.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';

import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { IEditEvents } from '../../../core/interfaces/event.interface';
import { ILocalEditContext } from '../core/interfaces/local-edit-context.interface';
/* eslint-disable no-underscore-dangle */
import { ILogger } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ISlideCountState } from '@src/slide/domain/slide-app/store/slide-count-state';
import { OperationScene } from '@src/slide/domain/slide-app/core/interfaces/qqmini-bridge-manager.interface';
import { QQMiniBridgeManager } from '@src/slide/domain/slide-app/core/managers/qqmini-bridge-manager';
import { TDWActionType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { getQuery } from 'sutils';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';

const CustomLoadPerformancePoint = {
    WEB_START_PARSE_HTML: 'web_start_parse_html',
    MAIN_START: 'main_start',
    REGISTER_LOCAL_EDIT_SERVICES: 'register_local_edit_services',
    LOAD_DOCUMENT_START: 'load_document_start',
};

export class LocalEditReporter {
    protected client = Client.create(
        {
            docx: Protocol<DocxServiceProtocol>(),
            report: Protocol<MegaReportServiceProtocol>(),
        },
        ENV.IS_MOBILE_LOCAL_EDIT,
    );

    private loadReportOptions: ReportLoadOptions;

    public constructor(
        protected logger: ILogger,
        protected context: ILocalEditContext,
        protected emitter: ILocalEditEvents,
        protected slideCountState: ISlideCountState,
        protected commonEmitter: IEditEvents,
    ) {
        this.loadReportOptions = {
            docType: DocType.Slide,
            type: 'load',
            globalPadId: this.context.getGlobalPadId(),
            csid: getQuery('csid'),
            useChunkImport: false,
            hasPassword: false,
            metrics: {},
            extra: {},
        };

        this.reportLoadMetric(LoadPerformancePoint.PARSE_HTML, performance?.timing?.responseEnd);
        this.reportLoadMetric(
            CustomLoadPerformancePoint.WEB_START_PARSE_HTML,
            (window as any).__webStartParseHtmlTimestamp,
        );
        this.reportLoadMetric(CustomLoadPerformancePoint.MAIN_START, window.jsRunMarkPoints?.mainStart);
        this.reportLoadMetric(
            CustomLoadPerformancePoint.REGISTER_LOCAL_EDIT_SERVICES,
            this.context.getLocalEditServicesRegisterTime(),
        );

        this.emitter.on(LocalEditEvents.LocalEditServiceInit, () => {
            this.reportLoadMetric(LoadPerformancePoint.INIT_LOCAL_EDIT);
        });

        this.emitter.on(LocalEditEvents.LoadDocumentStart, () => {
            this.reportLoadMetric(CustomLoadPerformancePoint.LOAD_DOCUMENT_START);
        });

        this.emitter.on(LocalEditEvents.LoadDocumentPasswordRequired, () => {
            this.loadReportOptions.hasPassword = true;
        });

        this.emitter.on(LocalEditEvents.LoadDocumentUseNativePassword, () => {
            this.loadReportOptions.hasPassword = true;
        });

        this.emitter.on(LocalEditEvents.LoadDocumentChunkReceived, () => {
            this.loadReportOptions.useChunkImport = true;
        });

        this.emitter.on(LocalEditEvents.LoadDocumentHeadSuccess, () => {
            this.reportLoadMetric(LoadPerformancePoint.FIRST_CHUNK_FETCHED);
        });

        this.emitter.on(LocalEditEvents.LoadDocumentAllChunksSuccess, () => {
            this.reportLoadMetric(LoadPerformancePoint.ALL_CHUNKS_FETCHED);
            // 移动端本地编辑分页数据全部拉取结束表示可编辑
            if (ENV.IS_MOBILE_LOCAL_EDIT) {
                this.reportLoadMetric(LoadPerformancePoint.CAN_EDIT);
            }
        });

        eventBus.onIncludingPast(EventKeys.FIRST_PAGE_DISPLAY, () => {
            // @ts-ignore
            // 演示模式下不重复上报
            if (window.isFirstPageDisplay) return;
            this.reportLoadMetric(LoadPerformancePoint.CAN_VIEW, (window as any).previewTime);
            this.reportLoadMetric(LoadPerformancePoint.CAN_INTERACT);
            // PC 客户端本地编辑 可见/可交互/可编辑为同一时机
            if (ENV.IS_PC_LOCAL_EDIT) {
                this.reportLoadMetric(LoadPerformancePoint.CAN_EDIT);
            }
        });

        this.emitter.on(LocalEditEvents.SaveDocumentSuccess, (saveType, traceKey, beginAt) => {
            this.report({
                docType: DocType.Slide,
                type: 'save',
                globalPadId: this.context.getGlobalPadId(),
                traceKey,
                metrics: {
                    [DumpPerformancePoint.BEGIN_DUMP]: beginAt,
                    [DumpPerformancePoint.FINISH_DUMP]: Date.now(),
                },
                extra: this.getExtraParams(),
            });
            QQMiniBridgeManager.getInstance().saveToFileCenter(saveType === SaveType.SaveAs
                ? OperationScene.LOCAL_SAVE_AS
                : OperationScene.LOCAL_SAVE);
        });

        this.emitter.on(LocalEditEvents.DocumentChanged, () => {
            tdw({
                opername: 'doc_core',
                module: 'edit',
                action: TDWActionType.EDIT_TEXT,
            });
        });
    }

    private reportLoadMetric(action: string, timestamp = Date.now()) {
        this.logger.info(
            '[LocalEditReporter]',
            `[${action.toUpperCase()}]`,
            `@${~~(timestamp - performance.timeOrigin)}`,
        );

        const { metrics } = this.loadReportOptions;
        metrics[action] = timestamp;

        /**
         * ALL_CHUNKS_FETCHED 和 FIST_PAGE_DISPLAY 先后顺序不固定
         * 因此在文档收集到所有关键数据时，再上报所有数据
         * 桌面端首屏可见即为 可见/可交互/可编辑 会导致 ALL_CHUNKS_FETCHED/FIST_PAGE_DISPLAY 两次触发上报
         * 所以调整为 ALL_CHUNKS_FETCHED && FIST_PAGE_DISPLAY 同时满足后再一次上报
         */
        if (
            metrics[LoadPerformancePoint.CAN_VIEW]
            && metrics[LoadPerformancePoint.CAN_INTERACT]
            && metrics[LoadPerformancePoint.CAN_EDIT]
            && metrics[LoadPerformancePoint.ALL_CHUNKS_FETCHED]
        ) {
            // 最终上报时再获取 slideCount
            this.loadReportOptions.extra = this.getExtraParams();
            this.report(this.loadReportOptions);
        }
    }

    private report(options: ReportOptions) {
        this.logger.info('[LocalEditReporter]', options.type, JSON.stringify(options));
        // 不支持新的上报方式
        if (!this.client.report.version) {
            return;
        }

        this.client.report.reportPerformance(options);
    }

    private getExtraParams() {
        return {
            name: this.context.getTitle(),
            slideCount: this.slideCountState.slideCount,
            releaseVersion: (window as any).__globalReleaseVersion,
            usedSsr: (window as any).previewTime ? 1 : 0,
        };
    }
}
